import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { FileClock, Flag, Timer, CheckCheck, Sparkles, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { Card } from "../components/ui/Card";
import { Kpi } from "../components/ui/Kpi";
import { Badge } from "../components/ui/Badge";

interface DashboardData {
  kpis: { pendingProposals: number; aiFlagged: number; avgDaysToApprove: number; bulkApprovalsThisWeek: number };
  tasks: Array<{ title: string; sub: string; view: string; tone: "amber" | "red" | "blue" }>;
  announcements: Array<{ id: number; level: "High" | "Medium" | "Normal"; message: string }>;
}

const dotTone = { amber: "bg-amber-500", red: "bg-red-500", blue: "bg-blue-500" } as const;
const announcementTone = { High: "red", Medium: "amber", Normal: "green" } as const;

export function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: () => api.get<DashboardData>("/dashboard") });

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-2">
        <Kpi label="Pending Proposals" value={isLoading ? "…" : data!.kpis.pendingProposals} sub="Across PO and SLA" icon={FileClock} tone="blue" />
        <Kpi label="AI-Flagged for Review" value={isLoading ? "…" : data!.kpis.aiFlagged} sub="Needs attention" icon={Flag} tone="amber" />
        <Kpi label="Avg Days to Approve" value={isLoading ? "…" : data!.kpis.avgDaysToApprove} sub="Rolling 30 days" icon={Timer} tone="green" />
        <Kpi label="Bulk Approvals This Week" value={isLoading ? "…" : data!.kpis.bulkApprovalsThisWeek} sub="Multi-select approvals" icon={CheckCheck} tone="slate" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2">
          <Card>
            <div className="p-5 border-b border-slate-100 dark:border-white/10 flex items-center justify-between">
              <div className="font-semibold text-sm">My Tasks</div>
              <span className="text-xs text-slate-400">Role: {user?.roles[0]}</span>
            </div>
            <div className="divide-y divide-slate-100 dark:divide-white/5">
              {data?.tasks.length ? (
                data.tasks.map((t, i) => (
                  <button key={i} onClick={() => navigate(`/${t.view}`)} className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-slate-50 dark:hover:bg-white/5 text-left">
                    <span className={`h-2 w-2 rounded-full shrink-0 ${dotTone[t.tone]}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{t.title}</div>
                      <div className="text-xs text-slate-400 truncate">{t.sub}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </button>
                ))
              ) : (
                <div className="px-5 py-6 text-sm text-slate-400">No pending tasks right now.</div>
              )}
            </div>
          </Card>
        </div>
        <div>
          <Card className="mb-4">
            <div className="p-5 border-b border-slate-100 dark:border-white/10 font-semibold text-sm">Announcements</div>
            <div className="p-5 space-y-3 text-sm">
              {data?.announcements.map((a) => (
                <div key={a.id} className="flex gap-2 items-start">
                  <div className="mt-0.5">
                    <Badge tone={announcementTone[a.level]}>{a.level}</Badge>
                  </div>
                  <div className="text-slate-600 dark:text-slate-300">{a.message}</div>
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <div className="p-5 flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-sbd-yellow" />
              <div className="text-sm">
                <div className="font-semibold">Try Ask GSP</div>
                <div className="text-slate-400 text-xs">"What's the difference between Confirm and Change Date access?"</div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
